import React from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { ADM, ADM_ICON_TONES, ADM_CHIP } from "./adminTheme";

function MiniSparkline({ series = [], color = ADM.blue, height = 28 }) {
  const vals = (Array.isArray(series) ? series : []).map((n) => Number(n) || 0);
  if (vals.length < 2) {
    return (
      <Box sx={{ height, width: "100%", opacity: 0.35 }}>
        <svg width="100%" height={height} viewBox="0 0 120 28" preserveAspectRatio="none">
          <line x1="0" y1="20" x2="120" y2="20" stroke={color} strokeWidth="2" strokeLinecap="round" />
        </svg>
      </Box>
    );
  }
  const max = Math.max(...vals, 1);
  const min = Math.min(...vals, 0);
  const span = Math.max(max - min, 1);
  const pts = vals
    .map((v, i) => {
      const x = (i / (vals.length - 1)) * 120;
      const y = 24 - ((v - min) / span) * 18;
      return `${x},${y}`;
    })
    .join(" ");
  return (
    <Box sx={{ height, width: "100%", mt: 1 }}>
      <svg width="100%" height={height} viewBox="0 0 120 28" preserveAspectRatio="none">
        <polyline fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" points={pts} />
      </svg>
    </Box>
  );
}

export function AdminIconTile({ tone = "blue", size = 36, children, sx }) {
  const t = ADM_ICON_TONES[tone] || ADM_ICON_TONES.blue;
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "10px",
        background: t.bg,
        color: t.fg,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export function AdminStatusChip({ variant = "active", label, size = "small" }) {
  const c = ADM_CHIP[variant] || ADM_CHIP.active;
  return (
    <Chip
      size={size}
      label={label}
      sx={{
        height: 24,
        fontWeight: 700,
        fontSize: 11,
        letterSpacing: "0.01em",
        background: c.bg,
        color: c.fg,
        borderRadius: "8px",
        "& .MuiChip-label": { px: 1.1 },
      }}
    />
  );
}

export function AdminMetricCard({
  icon,
  tone = "blue",
  value,
  label,
  sublabel,
  trend,
  series,
  sparkColor,
  showSpark = true,
}) {
  const t = ADM_ICON_TONES[tone] || ADM_ICON_TONES.blue;
  return (
    <Paper
      elevation={0}
      sx={{
        p: "16px 18px 12px",
        borderRadius: `${ADM.radius}px`,
        border: `1px solid ${ADM.border}`,
        background: ADM.card,
        boxShadow: ADM.shadow,
        minHeight: showSpark ? 128 : 112,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        transition: "border-color .14s ease",
        "&:hover": {
          borderColor: t.fg,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
        <AdminIconTile tone={tone}>{icon}</AdminIconTile>
        {trend ? (
          <Typography
            component="span"
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: typeof trend === "string" && /deliver|up|\+|▲|active|live/i.test(trend) ? ADM.green : ADM.textMuted,
            }}
          >
            {trend}
          </Typography>
        ) : null}
      </Box>
      <Typography sx={{ mt: 1.25, fontSize: { xs: 19, sm: 22, md: 24 }, fontWeight: 650, letterSpacing: "-0.02em", color: ADM.text, lineHeight: 1.15, wordBreak: "break-word" }}>
        {value}
      </Typography>
      <Typography sx={{ mt: 0.4, fontSize: 12, fontWeight: 400, color: ADM.textMuted }}>{label}</Typography>
      {sublabel ? (
        <Typography sx={{ fontSize: 11, color: ADM.textSoft, mt: 0.15 }}>{sublabel}</Typography>
      ) : null}
      {showSpark ? (
        <Box sx={{ mt: "auto", pt: 0.5 }}>
          <MiniSparkline series={series} color={sparkColor || t.fg} />
        </Box>
      ) : (
        <Box sx={{ mt: "auto" }} />
      )}
    </Paper>
  );
}

export function AdminControlBanner({
  operational = true,
  metrics = [],
  title = "Avaada control center",
  desc = "Real-time platform overview for meetings, users, and secured services.",
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: `${ADM.radius}px`,
        background: `linear-gradient(135deg, ${ADM.mint} 0%, #FAFBFC 55%, #FFFFFF 100%)`,
        color: ADM.text,
        p: { xs: "18px 18px 16px", md: "22px 24px 18px" },
        mb: 2,
        border: `1px solid ${ADM.border}`,
        boxShadow: ADM.shadow,
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.85,
          px: 1.25,
          py: 0.45,
          borderRadius: 999,
          background: operational ? "rgba(21,128,61,0.1)" : "rgba(220,38,38,0.1)",
          border: operational ? "1px solid rgba(21,128,61,0.2)" : "1px solid rgba(220,38,38,0.2)",
          mb: 1.25,
        }}
      >
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: operational ? "#22C55E" : "#EF4444",
            boxShadow: operational ? "0 0 0 3px rgba(34,197,94,0.2)" : "none",
            animation: operational ? "admPulse 1.8s ease infinite" : "none",
            "@keyframes admPulse": {
              "0%,100%": { opacity: 1 },
              "50%": { opacity: 0.45 },
            },
          }}
        />
        <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.06em", color: operational ? "#15803D" : "#B91C1C" }}>
          {operational ? "ALL SYSTEMS OPERATIONAL" : "ATTENTION REQUIRED"}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: "-0.03em", color: ADM.text, mb: 0.5 }}>
        {title}
      </Typography>
      {desc ? (
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 500,
            color: ADM.textMuted,
            mb: metrics.length ? 2 : 0,
            maxWidth: 640,
            lineHeight: 1.5,
          }}
        >
          {desc}
        </Typography>
      ) : (
        <Box sx={{ mb: metrics.length ? 1.5 : 0 }} />
      )}
      {metrics.length ? (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2,1fr)", sm: "repeat(4,1fr)" },
          gap: { xs: 2, md: 3 },
        }}
      >
        {metrics.map((m) => (
          <Box key={m.label}>
            <Typography sx={{ fontSize: 28, fontWeight: 800, letterSpacing: "-0.03em", color: ADM.text, lineHeight: 1.1 }}>
              {m.value}
            </Typography>
            <Typography
              sx={{
                mt: 0.6,
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: ADM.textMuted,
              }}
            >
              {m.label}
            </Typography>
          </Box>
        ))}
      </Box>
      ) : null}
    </Paper>
  );
}

export function AdminListCard({ title, actionLabel, onAction, toolbar, children, sx }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: `${ADM.radius}px`,
        border: `1px solid ${ADM.border}`,
        background: ADM.card,
        boxShadow: ADM.shadow,
        overflow: "hidden",
        ...sx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${ADM.borderSoft}`,
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 800, letterSpacing: "-0.02em", color: ADM.text }}>{title}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          {toolbar || null}
          {actionLabel ? (
            <Button
              size="small"
              onClick={onAction}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: 12,
                color: ADM.teal,
                minWidth: 0,
                px: 1,
              }}
            >
              {actionLabel}
            </Button>
          ) : null}
        </Box>
      </Box>
      <Box>{children}</Box>
    </Paper>
  );
}

export function AdminMeetingRow({ initials, title, meta, chipLabel, chipVariant = "summarized", onClick }) {
  return (
    <Box
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.35,
        borderBottom: `1px solid ${ADM.borderSoft}`,
        cursor: onClick ? "pointer" : "default",
        "&:last-child": { borderBottom: "none" },
        "&:hover": onClick ? { background: ADM.pageBg } : undefined,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(145deg, ${ADM.teal} 0%, #1D546C 55%, #0C8F55 100%)`,
          color: "#fff",
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: "0.02em",
        }}
      >
        {String(initials || "?").slice(0, 2).toUpperCase()}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: ADM.text,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Typography>
        {meta ? (
          <Typography
            sx={{
              mt: 0.2,
              fontSize: 11.5,
              color: ADM.textMuted,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {meta}
          </Typography>
        ) : null}
      </Box>
      {chipLabel ? <AdminStatusChip variant={chipVariant} label={chipLabel} /> : null}
    </Box>
  );
}

export function AdminSystemRow({ label, detail, ok }) {
  const variant = ok === true ? "active" : ok === false ? "error" : "standby";
  const chipLabel = ok === true ? "Active" : ok === false ? "Needs attention" : "Standby";
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        px: 2,
        py: 1.35,
        borderBottom: `1px solid ${ADM.borderSoft}`,
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Box sx={{ minWidth: 0, display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            mt: "5px",
            flexShrink: 0,
            background: ok === true ? "#22C55E" : ok === false ? "#EF4444" : "#CBD5E1",
          }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 650, color: ADM.text }}>{label}</Typography>
          {detail ? (
            <Typography sx={{ fontSize: 11, color: ADM.textSoft, mt: 0.2 }}>{detail}</Typography>
          ) : null}
        </Box>
      </Box>
      <AdminStatusChip variant={variant} label={chipLabel} />
    </Box>
  );
}

export function AdminPageHero({ badge, badgeIcon, title, desc, meta }) {
  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        p: "18px 20px",
        borderRadius: `${ADM.radius}px`,
        border: `1px solid ${ADM.border}`,
        background: `linear-gradient(135deg, ${ADM.mint} 0%, #FFFFFF 72%)`,
        boxShadow: ADM.shadow,
      }}
    >
      {badge ? (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.1,
            py: 0.4,
            mb: 1,
            borderRadius: 999,
            background: ADM.tealSoft,
            color: ADM.teal,
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {badgeIcon}
          {badge}
        </Box>
      ) : null}
      <Typography sx={{ m: 0, fontSize: 21, fontWeight: 650, letterSpacing: "-0.02em", color: ADM.text, lineHeight: 1.25, wordBreak: "break-word" }}>
        {title}
      </Typography>
      {desc ? (
        <Typography sx={{ mt: 0.85, fontSize: { xs: 12, sm: 13 }, lineHeight: 1.55, color: ADM.textMuted, maxWidth: 680 }}>{desc}</Typography>
      ) : null}
      {meta ? (
        <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, fontSize: 11.5, color: ADM.textMuted }}>
          {meta}
        </Box>
      ) : null}
    </Paper>
  );
}
